import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Loader2, AlertCircle } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useUserStore } from '@/store/userStore';
import { useTelegram } from '@/hooks/useTelegram';
import {
  EMPHASIS_META,
  LEVEL_META,
  type WorkoutLevel,
  type WorkoutEmphasis,
  type PlanQuestionnaire,
  type PlanData,
} from '@/types/workout';

const ENDPOINT = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-workout`;

const STEPS = ['days', 'level', 'emphasis', 'equipment', 'limitations'] as const;
type Step = typeof STEPS[number];

const ALL_EQUIPMENT = ['body_weight', 'dumbbell', 'barbell', 'cable', 'machine'];

const EQUIPMENT_OPTIONS = [
  { id: 'gym',         label: 'Фитнес-клуб',  emoji: '🏟️', sub: 'Всё оборудование', all: true },
  { id: 'body_weight', label: 'Только тело',   emoji: '🤸', sub: 'Без инвентаря' },
  { id: 'dumbbell',   label: 'Гантели',        emoji: '🏋️', sub: 'Гантели и скамья' },
  { id: 'barbell',    label: 'Штанга',          emoji: '🔩', sub: 'Штанга и стойка' },
  { id: 'cable',      label: 'Кроссовер',      emoji: '🔗', sub: 'Блочный тренажёр' },
  { id: 'machine',    label: 'Тренажёры',      emoji: '⚙️', sub: 'Силовые тренажёры' },
];

export default function WorkoutPlanScreen() {
  const nav = useNavigate();
  const { profile } = useUserStore();
  const { haptic } = useTelegram();

  const [step, setStep] = useState<Step>('days');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showWarning, setShowWarning] = useState(false);

  const [form, setForm] = useState<PlanQuestionnaire>({
    days_per_week: 'optimal',
    level: 'beginner',
    emphasis: 'balanced',
    equipment: ['body_weight'],
    limitations: '',
  });

  const stepIndex = STEPS.indexOf(step);

  function next() {
    haptic('light');
    // Предупреждение при 1 тренировке в неделю
    if (step === 'days' && form.days_per_week === 1 && !showWarning) {
      setShowWarning(true);
      return;
    }
    setShowWarning(false);
    if (stepIndex < STEPS.length - 1) setStep(STEPS[stepIndex + 1]);
    else generatePlan();
  }

  function prev() {
    haptic('light');
    setShowWarning(false);
    if (stepIndex > 0) setStep(STEPS[stepIndex - 1]);
    else nav('/chat?tab=workout');
  }

  function toggleEquipment(id: string) {
    haptic('light');
    if (id === 'gym') {
      setForm((f) => ({ ...f, equipment: ALL_EQUIPMENT }));
      return;
    }
    setForm((f) => ({
      ...f,
      equipment: f.equipment.includes(id)
        ? f.equipment.filter((e) => e !== id)
        : f.equipment.filter((e) => e !== 'gym_placeholder').concat(id),
    }));
  }

  async function generatePlan() {
    if (!profile?.id) return;
    setGenerating(true);
    setError(null);

    const actualDays = form.days_per_week === 'optimal' ? 3 : form.days_per_week;

    try {
      // Маппинг UI-значений оборудования → реальные значения в базе
      const EQUIPMENT_DB_MAP: Record<string, string[]> = {
        body_weight: ['body weight'],
        dumbbell:    ['dumbbell', 'kettlebell'],
        barbell:     ['barbell', 'ez barbell', 'trap bar'],
        cable:       ['cable', 'resistance band'],
        machine:     ['leverage machine', 'smith machine', 'sled machine', 'weighted', 'upper body ergometer', 'wheel roller'],
      };
      const dbEquipment = [...new Set(
        form.equipment.flatMap((e) => EQUIPMENT_DB_MAP[e] ?? [e])
      )];

      // Запрашиваем упражнения из базы для передачи AI
      const { data: exercises } = await supabase
        .from('exercises')
        .select('id, name, category, equipment, target, muscle_group')
        .in('equipment', dbEquipment)
        .limit(400);

      const exercisesForAI = exercises ?? [];

      // Вызываем AI
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token ?? import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          days_per_week: actualDays,
          level: form.level,
          emphasis: form.emphasis,
          equipment: form.equipment,
          limitations: form.limitations || null,
          current_week: 1,
          exercises: exercisesForAI,
        }),
      });

      if (!res.ok) throw new Error(`AI error ${res.status}`);
      const planData: PlanData = await res.json();

      // Сохраняем план в Supabase
      const emphasisLabel = EMPHASIS_META[form.emphasis].label;
      const levelLabel = LEVEL_META[form.level].label;
      const planName = `${levelLabel} · ${emphasisLabel}`;

      const { data: plan, error: planError } = await supabase
        .from('workout_plans')
        .insert({
          user_id: profile.id,
          name: planName,
          goal: profile.goal ?? 'maintain',
          level: form.level,
          days_per_week: actualDays,
          emphasis: form.emphasis,
          weekly_sets_per_group: LEVEL_META[form.level].sets_per_group,
          equipment: form.equipment,
          limitations: form.limitations || null,
          plan_data: planData,
          is_active: true,
          current_week: 1,
        })
        .select()
        .single();

      if (planError) throw new Error(planError.message);

      haptic('success');
      nav(`/workout/plan/${plan.id}`, { replace: true });
    } catch (e: any) {
      setError(e.message ?? 'Ошибка генерации плана');
      setGenerating(false);
    }
  }

  if (generating) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
        >
          <Loader2 className="w-10 h-10 text-emerald-glow" />
        </motion.div>
        <div className="text-center">
          <div className="font-semibold text-base">Составляю план...</div>
          <div className="text-[12px] text-ink-muted mt-1">AI подбирает упражнения из базы</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* Хедер */}
      <div className="flex items-center gap-3 px-4 pt-4 pb-2">
        <button onClick={prev} className="p-2 -ml-2">
          <ChevronLeft size={20} />
        </button>
        <div className="flex-1">
          <div className="font-semibold text-sm">Новый план</div>
          <div className="text-[10px] text-ink-dim">Шаг {stepIndex + 1} из {STEPS.length}</div>
        </div>
      </div>

      {/* Прогресс-бар */}
      <div className="h-0.5 bg-line mx-4 rounded-full mb-6">
        <motion.div
          className="h-full bg-emerald-glow rounded-full"
          animate={{ width: `${((stepIndex + 1) / STEPS.length) * 100}%` }}
          transition={{ duration: 0.3 }}
        />
      </div>

      <div className="flex-1 px-4 overflow-y-auto">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
          >
            {/* ШАГ 1: Дни в неделю */}
            {step === 'days' && (
              <div className="space-y-4">
                <div>
                  <h2 className="font-display font-bold text-xl">Сколько раз в неделю?</h2>
                  <p className="text-[12px] text-ink-muted mt-1">Выбери или доверь нам</p>
                </div>

                {/* Предупреждение для 1 раза */}
                {showWarning && (
                  <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex gap-2 p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/30"
                  >
                    <AlertCircle size={16} className="text-yellow-400 shrink-0 mt-0.5" />
                    <div className="text-[11px] text-ink-muted leading-relaxed">
                      При 1 тренировке весь объём концентрируется в одной сессии — это займёт 60–90 минут. Для лучшего результата рекомендуем 2–3 раза.
                    </div>
                  </motion.div>
                )}

                <div className="space-y-2.5">
                  {[
                    { v: 'optimal' as const, label: 'Оптимальная', sub: '3 раза — лучший баланс результата и восстановления', accent: true },
                    { v: 1, label: '1 раз', sub: 'Минимум, одна большая сессия' },
                    { v: 2, label: '2 раза', sub: 'Хорошее начало, достаточно восстановления' },
                    { v: 3, label: '3 раза', sub: 'Оптимально для большинства целей' },
                  ].map(({ v, label, sub, accent }) => (
                    <button
                      key={String(v)}
                      onClick={() => { haptic('light'); setForm((f) => ({ ...f, days_per_week: v })); setShowWarning(false); }}
                      className={`w-full text-left p-4 rounded-2xl border transition-all ${
                        form.days_per_week === v
                          ? 'border-emerald-glow bg-emerald-glow/10'
                          : 'border-line bg-bg-card'
                      }`}
                    >
                      <div className={`font-semibold text-sm ${accent ? 'text-emerald-glow' : ''}`}>
                        {label} {accent && '✨'}
                      </div>
                      <div className="text-[11px] text-ink-muted mt-0.5">{sub}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* ШАГ 2: Уровень */}
            {step === 'level' && (
              <div className="space-y-4">
                <div>
                  <h2 className="font-display font-bold text-xl">Твой уровень?</h2>
                  <p className="text-[12px] text-ink-muted mt-1">Определяет стартовый объём нагрузки</p>
                </div>
                <div className="space-y-2.5">
                  {(Object.entries(LEVEL_META) as [WorkoutLevel, typeof LEVEL_META[WorkoutLevel]][]).map(([key, meta]) => (
                    <button
                      key={key}
                      onClick={() => { haptic('light'); setForm((f) => ({ ...f, level: key })); }}
                      className={`w-full text-left p-4 rounded-2xl border transition-all ${
                        form.level === key ? 'border-emerald-glow bg-emerald-glow/10' : 'border-line bg-bg-card'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="font-semibold text-sm">{meta.label}</div>
                        <div className="text-[10px] text-ink-dim bg-bg-elevated px-2 py-0.5 rounded-full">
                          {meta.sets_per_group}+ подх./нед
                        </div>
                      </div>
                      <div className="text-[11px] text-ink-muted mt-1">{meta.description}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* ШАГ 3: Акцент */}
            {step === 'emphasis' && (
              <div className="space-y-4">
                <div>
                  <h2 className="font-display font-bold text-xl">Акцент нагрузки</h2>
                  <p className="text-[12px] text-ink-muted mt-1">Всё тело тренируется каждую сессию, акцент получает больше работы</p>
                </div>
                <div className="space-y-2.5">
                  {(Object.entries(EMPHASIS_META) as [WorkoutEmphasis, typeof EMPHASIS_META[WorkoutEmphasis]][]).map(([key, meta]) => (
                    <button
                      key={key}
                      onClick={() => { haptic('light'); setForm((f) => ({ ...f, emphasis: key })); }}
                      className={`w-full text-left p-4 rounded-2xl border transition-all ${
                        form.emphasis === key ? 'border-emerald-glow bg-emerald-glow/10' : 'border-line bg-bg-card'
                      }`}
                    >
                      <div className="font-semibold text-sm">{meta.label}</div>
                      <div className="text-[11px] text-ink-muted mt-0.5">{meta.description}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* ШАГ 4: Оборудование */}
            {step === 'equipment' && (
              <div className="space-y-4">
                <div>
                  <h2 className="font-display font-bold text-xl">Какое оборудование есть?</h2>
                  <p className="text-[12px] text-ink-muted mt-1">Можно выбрать несколько</p>
                </div>
                <div className="space-y-2.5">
                  {EQUIPMENT_OPTIONS.map(({ id, label, emoji, sub, all }) => {
                    const isGym = id === 'gym';
                    const isSelected = isGym
                      ? ALL_EQUIPMENT.every((e) => form.equipment.includes(e))
                      : form.equipment.includes(id);
                    return (
                      <button
                        key={id}
                        onClick={() => toggleEquipment(id)}
                        className={`w-full flex items-center gap-3 p-4 rounded-2xl border transition-all ${
                          isSelected
                            ? 'border-emerald-glow bg-emerald-glow/10'
                            : 'border-line bg-bg-card'
                        } ${isGym ? 'ring-1 ring-emerald-glow/20' : ''}`}
                      >
                        <span className="text-2xl">{emoji}</span>
                        <div className="flex-1 text-left">
                          <div className="font-semibold text-sm">{label}</div>
                          {sub && <div className="text-[10px] text-ink-dim mt-0.5">{sub}</div>}
                        </div>
                        {isSelected && (
                          <span className="text-emerald-glow text-base">✓</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ШАГ 5: Ограничения */}
            {step === 'limitations' && (
              <div className="space-y-4">
                <div>
                  <h2 className="font-display font-bold text-xl">Есть ограничения?</h2>
                  <p className="text-[12px] text-ink-muted mt-1">Травмы, боли, противопоказания — AI учтёт при подборе упражнений</p>
                </div>
                <textarea
                  value={form.limitations}
                  onChange={(e) => setForm((f) => ({ ...f, limitations: e.target.value }))}
                  placeholder="Например: боль в колене, проблемы с поясницей, после операции на плече..."
                  rows={4}
                  className="w-full bg-bg-card border border-line rounded-xl p-3 text-sm placeholder:text-ink-dim outline-none resize-none focus:border-emerald-glow/50 transition-colors"
                />
                <div
                  onClick={() => { haptic('light'); setForm((f) => ({ ...f, limitations: '' })); }}
                  className="text-[11px] text-ink-dim"
                >
                  Или <span className="text-emerald-glow">пропустить →</span> если ограничений нет
                </div>

                {/* Итоговый дисклеймер */}
                <div className="bg-bg-elevated border border-line rounded-xl p-3">
                  <p className="text-[10px] text-ink-dim leading-relaxed">
                    ⚠️ План составляется AI на основе твоих ответов и носит ознакомительный характер. Обязательно начинай с разминки. Для максимальной эффективности и безопасности обратись к сертифицированному тренеру.
                  </p>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {error && (
          <div className="mt-4 p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-[12px] text-red-400">
            {error}
          </div>
        )}
      </div>

      {/* Кнопка далее */}
      <div className="px-4 py-4 border-t border-line">
        <button
          onClick={next}
          disabled={form.equipment.length === 0}
          className="w-full bg-emerald-glow text-bg font-semibold py-3.5 rounded-2xl flex items-center justify-center gap-2 active:scale-[0.98] transition-all disabled:opacity-40"
        >
          {stepIndex === STEPS.length - 1 ? (
            <>Составить план ✨</>
          ) : (
            <>Далее <ChevronRight size={16} /></>
          )}
        </button>
      </div>
    </div>
  );
}
